import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

import { OnboardingToolMarquee } from './onboarding-tool-marquee.atom'

interface OnboardingBackgroundProps {
  toolNameList?: string[]
  className?: string
}

/**
 * Decorative background layer with soft gradient orbs, a warm-to-cool gradient mesh,
 * and an optional diagonal marquee of tool names.
 * Purely visual — no interactivity. Designed for use behind the onboarding wizard overlay.
 */
export const OnboardingBackground: React.FC<OnboardingBackgroundProps> = ({
  toolNameList,
  className,
}): ReactNode => (
  <div
    aria-hidden="true"
    className={cn(
      'onboarding-background pointer-events-none absolute inset-0 select-none overflow-hidden',
      className
    )}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-orange-50/60 via-white/40 to-violet-50/50 dark:from-orange-950/20 dark:via-background/10 dark:to-violet-950/20" />

    {toolNameList && toolNameList.length > 0 && (
      <OnboardingToolMarquee toolNameList={toolNameList} />
    )}

    <div className="animate-onboarding-float-1 absolute -top-24 -left-24 h-[28rem] w-[28rem] rounded-full bg-[var(--saaga-primary-100)] opacity-[0.14] blur-3xl dark:bg-[var(--saaga-primary-800)] dark:opacity-[0.08]" />

    <div className="animate-onboarding-float-2 absolute -top-16 -right-20 h-[24rem] w-[24rem] rounded-full bg-violet-200 opacity-[0.12] blur-3xl dark:bg-violet-800 dark:opacity-[0.07]" />

    <div className="animate-onboarding-float-3 absolute -bottom-20 -left-16 h-[22rem] w-[22rem] rounded-full bg-sky-100 opacity-[0.10] blur-3xl dark:bg-sky-900 dark:opacity-[0.06]" />

    <div className="animate-onboarding-float-2 absolute right-1/4 bottom-1/4 h-[18rem] w-[18rem] rounded-full bg-pink-100 opacity-[0.08] blur-3xl dark:bg-pink-900 dark:opacity-[0.05]" />

    <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_40%,var(--saaga-primary-50)_0%,transparent_70%)] opacity-30 dark:opacity-10" />
  </div>
)
